import { Headline, Paragraph } from '@n2-common/ui';

import { getApplicationStatus, submitSsnToGetGpid } from '@n2-common/services/legacyApplicationApiService';

import { useNavigate } from 'react-router-dom';

import style from './style.module.scss';
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { navigateToCorrectStep } from '../../App';
import Wizard from '@n2-common/components/MultiStageOnePageWizard';
import { useApplicationIdContext } from '@n2-common/components/ApplicationContext';
import { navigateToStep, ApplicationPaths } from '@n2-common/applicationPages/ApplicationFormsHelpers';
import { trackCoreBankEvent, trackEcommerceEvent } from '@n2-common/lib/gtm';
import { SubmitFunc } from '@n2-common/components/MultiStageOnePageWizard/types';
import { errorToAction } from '@n2-common/applicationPages/applicationErrorHelper';
import { useWizardFormManager } from '@n2-common/components/WizardFormManager';
import { useTranslation } from 'react-i18next';
import { useLoanApplicationContext } from './Context';
import { useBackendContext } from '@n2-common/components/BackendContext';
import { LoanApplicationContextProvider as O1LoanApplicationContextProvider } from './Context.O1';
import { LoanApplicationContextProvider as O2LoanApplicationContextProvider } from './Context.O2';

export default function WizardApp() {
    const { provider } = useBackendContext();
    return provider != 'O2' ? (
        <O1LoanApplicationContextProvider>
            <WizardAppComponent />
        </O1LoanApplicationContextProvider>
    ) : (
        <O2LoanApplicationContextProvider>
            <WizardAppComponent />
        </O2LoanApplicationContextProvider>
    );
}

function WizardAppComponent() {
    const { t } = useTranslation('common');
    const { initialValues, getWizardStages, mapForSubmit } = useWizardFormManager('NO-CL');
    const formName = 'NO_LOAN';
    const { postLoanApplication, redirect } = useLoanApplicationContext();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { applicationId, applicantId, country, productType } = useApplicationIdContext(false, false);
    const [gpid, setGpid] = useState('');

    useEffect(() => {
        document.title = 'Lån uten sikkerhet';
        trackCoreBankEvent(productType, country, 'application_start');
        trackEcommerceEvent(productType, country, 'view_item', 0);
    }, [country, productType]);
    useEffect(() => {
        if (!(applicantId && applicationId)) {
            return;
        }

        getApplicationStatus(applicationId, applicantId).then((res) => {
            navigateToCorrectStep(res, applicantId, navigate);
        });
    }, [applicationId, navigate, applicantId]);

    const stages = useMemo(() => getWizardStages(t), [getWizardStages, t]);
    stages[stages.length - 1].afterStageElement = getAfterStageElement();

    const onSsnStepFinished = useCallback(
        async (values: typeof initialValues) => {
            const {
                Applicant: { NationalId: nationalId },
                DesiredCredit: desiredCredit,
            } = values;

            try {
                const [, res] = await submitSsnToGetGpid({ Ssn: nationalId });
                startTransition(() => setGpid(res.Gpid));

                trackEcommerceEvent(productType, country, 'begin_checkout', parseInt(desiredCredit, 10), res.Gpid);
            } catch (err) {
                console.error(err);
                return;
            }
        },
        [country, productType]
    );

    const onSubmit: SubmitFunc<typeof initialValues> = useCallback(
        async (values, setSubmitState) => {
            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);
            const cleanedValues = { ...mapForSubmit(values), Gpid: gpid };
            startTransition(() => setSubmitState('Submitted'));
            try {
                startTransition(() => setSubmitState('Processing'));

                const loanAmountForTracking = parseInt(cleanedValues.DesiredCredit?.replace(/\s/g, ''));
                trackCoreBankEvent(productType, country, 'application_sent', undefined, undefined, false, loanAmountForTracking);

                const result = await postLoanApplication(cleanedValues);
                redirect(result, navigate, false);
            } catch (err) {
                const action = errorToAction(err, formName, applicationId, applicantId, 'O2');
                if (action) {
                    setSubmitState({ type: 'Error', options: action });
                    setIsSubmitting(false);
                } else {
                    navigateToStep(ApplicationPaths.error, undefined, navigate, undefined);
                }
            }
        },
        [isSubmitting, mapForSubmit, gpid, productType, country, postLoanApplication, redirect, navigate, applicationId, applicantId]
    );

    return (
        <>
            <Headline>Lån uten sikkerhet</Headline>
            <Paragraph>
                Søknaden er personlig og behandles på grunnlag av dine oppgitte og våre innhentede opplysninger. Månedsbeløp og rente fastsettes
                individuelt og vises etter innsendt søknad.
            </Paragraph>
            <div className={style.form}>
                <Wizard
                    majorNumberUsedForTracking={'1'}
                    formName="no_loan_step1_partial"
                    initialValues={initialValues}
                    stages={stages}
                    onSubmit={onSubmit}
                    onSsnStepFinished={onSsnStepFinished}
                    ssnStep={1}
                    initialStep={1}
                />
            </div>
        </>
    );
}

const getAfterStageElement = () => {
    return <p>Ved å trykke "Send inn" godtar jeg at det blir foretatt en kredittsjekk, samt et oppslag hos gjeldsinformasjonsforetak.</p>;
};
